import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  Alert
} from '@mui/material';
import {
  Security,
  LocalHospital,
  Lock,
  Group,
  Storage,
  Share,
  Update,
  Email,
  Shield,
  Visibility,
  Delete
} from '@mui/icons-material';

const PrivacyPolicy = () => {
  const lastUpdated = "December 2024";

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Paper sx={{ p: 4, mb: 3, textAlign: 'center', bgcolor: 'primary.50' }}>
        <Security sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
        <Typography variant="h3" component="h1" gutterBottom color="primary.main">
          Privacy Policy
        </Typography>
        <Typography variant="h6" color="text.secondary">
          BD Dashboard - Healthcare CRM System
        </Typography>
        <Chip 
          label={`Last Updated: ${lastUpdated}`} 
          color="primary" 
          variant="outlined" 
          sx={{ mt: 2 }}
        />
      </Paper>

      {/* Introduction */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalHospital color="primary" />
            Introduction
          </Typography>
          
          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
            BD Dashboard ("we," "our," or "us") is committed to protecting the privacy and security of 
            your personal information. This Privacy Policy explains how we collect, use, disclose, and 
            safeguard your information when you use our healthcare CRM platform.
          </Typography>

          <Typography variant="body1" paragraph>
            As a healthcare technology provider, we understand the sensitive nature of medical information 
            and are committed to maintaining the highest standards of data protection in compliance with 
            applicable privacy laws and healthcare regulations.
          </Typography>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Healthcare Compliance:</strong> Our platform is designed to comply with healthcare 
              privacy regulations including HIPAA (Health Insurance Portability and Accountability Act) 
              where applicable.
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      {/* Information We Collect */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Storage color="primary" />
            Information We Collect
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            1. Personal Information
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><Group color="success" /></ListItemIcon>
              <ListItemText
                primary="Account Information"
                secondary="Name, email address, phone number, job title, organization details"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><LocalHospital color="success" /></ListItemIcon>
              <ListItemText
                primary="Professional Information"
                secondary="Medical specialization, license information, healthcare facility details"
              />
            </ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            2. Patient-Related Information
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><Security color="warning" /></ListItemIcon>
              <ListItemText
                primary="Lead Information"
                secondary="Patient contact details, medical conditions, appointment history"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Security color="warning" /></ListItemIcon>
              <ListItemText
                primary="Communication Records"
                secondary="Call logs, follow-up notes, treatment communications"
              />
            </ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            3. Technical Information
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><Storage color="info" /></ListItemIcon>
              <ListItemText
                primary="Usage Data"
                secondary="Login activities, feature usage, system interactions, performance metrics"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Storage color="info" /></ListItemIcon>
              <ListItemText
                primary="Device Information"
                secondary="Browser type, device identifiers, IP addresses, operating system"
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* How We Use Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Visibility color="primary" />
            How We Use Your Information
          </Typography>

          <List>
            <ListItem>
              <ListItemIcon><LocalHospital color="primary" /></ListItemIcon>
              <ListItemText
                primary="Healthcare Services"
                secondary="Managing patient leads, scheduling appointments, tracking medical communications"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Group color="primary" /></ListItemIcon>
              <ListItemText
                primary="Account Management"
                secondary="Creating and maintaining user accounts, authentication, user support"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Security color="primary" /></ListItemIcon>
              <ListItemText
                primary="Platform Security"
                secondary="Monitoring for fraud, ensuring system security, preventing unauthorized access"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Update color="primary" /></ListItemIcon>
              <ListItemText
                primary="Service Improvement"
                secondary="Analyzing usage patterns, developing new features, enhancing user experience"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Email color="primary" /></ListItemIcon>
              <ListItemText
                primary="Communications"
                secondary="System notifications, service updates, support communications"
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Data Security */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Shield color="primary" />
            Data Security & Protection
          </Typography>

          <Typography variant="body1" paragraph>
            We implement industry-standard security measures to protect your information:
          </Typography>

          <List>
            <ListItem>
              <ListItemIcon><Lock color="success" /></ListItemIcon>
              <ListItemText
                primary="Encryption"
                secondary="All data is encrypted in transit and at rest using advanced encryption standards"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Security color="success" /></ListItemIcon>
              <ListItemText
                primary="Access Controls"
                secondary="Role-based access controls ensure only authorized personnel can access sensitive data"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Storage color="success" /></ListItemIcon>
              <ListItemText
                primary="Secure Infrastructure"
                secondary="Our servers are hosted in secure, compliant data centers with regular security audits"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Update color="success" /></ListItemIcon>
              <ListItemText
                primary="Regular Updates"
                secondary="We regularly update our security systems and conduct vulnerability assessments"
              />
            </ListItem>
          </List>

          <Alert severity="success" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>HIPAA Compliance:</strong> We maintain administrative, physical, and technical 
              safeguards that comply with HIPAA requirements for protecting electronic health information.
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      {/* Information Sharing */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Share color="primary" />
            Information Sharing & Disclosure
          </Typography>

          <Typography variant="body1" paragraph>
            We do not sell, trade, or rent your personal information to third parties. We may share 
            information only in the following circumstances:
          </Typography>

          <List>
            <ListItem>
              <ListItemIcon><Group color="info" /></ListItemIcon>
              <ListItemText
                primary="Within Your Organization"
                secondary="Information is shared with authorized team members within your healthcare organization"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Security color="warning" /></ListItemIcon>
              <ListItemText
                primary="Legal Requirements"
                secondary="When required by law, court order, or government regulation"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Shield color="warning" /></ListItemIcon>
              <ListItemText
                primary="Service Providers"
                secondary="With trusted third-party service providers who assist in platform operations (under strict confidentiality agreements)"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><LocalHospital color="success" /></ListItemIcon>
              <ListItemText
                primary="Healthcare Partners"
                secondary="With healthcare providers involved in patient care, only with proper authorization"
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Your Rights */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Security color="primary" />
            Your Privacy Rights
          </Typography>

          <Typography variant="body1" paragraph>
            You have the following rights regarding your personal information:
          </Typography>

          <List>
            <ListItem>
              <ListItemIcon><Visibility color="primary" /></ListItemIcon>
              <ListItemText
                primary="Access & Portability"
                secondary="Request access to your personal data and receive a copy in a structured format"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Update color="primary" /></ListItemIcon>
              <ListItemText
                primary="Correction"
                secondary="Request correction of inaccurate or incomplete personal information"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Delete color="error" /></ListItemIcon>
              <ListItemText
                primary="Deletion"
                secondary="Request deletion of your personal data (subject to legal and medical record retention requirements)"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Lock color="warning" /></ListItemIcon>
              <ListItemText
                primary="Processing Restrictions"
                secondary="Request limitations on how we process your information"
              />
            </ListItem>
          </List>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              To exercise these rights, please contact us at{' '}
              <strong>bddashboardcrm@gmail.com</strong>
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      {/* Data Retention */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Storage color="primary" />
            Data Retention
          </Typography>

          <Typography variant="body1" paragraph>
            We retain your information for as long as necessary to provide our services and comply 
            with legal obligations:
          </Typography>

          <List>
            <ListItem>
              <ListItemIcon><Group color="info" /></ListItemIcon>
              <ListItemText
                primary="Account Information"
                secondary="Retained while your account is active and for 3 years after deactivation"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><LocalHospital color="warning" /></ListItemIcon>
              <ListItemText
                primary="Medical Records"
                secondary="Retained according to medical record retention laws (typically 7-10 years)"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Security color="success" /></ListItemIcon>
              <ListItemText
                primary="Security Logs"
                secondary="Retained for 2 years for security and audit purposes"
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Contact & Updates */}
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Email color="primary" />
            Contact Us & Policy Updates
          </Typography>

          <Typography variant="body1" paragraph>
            If you have questions about this Privacy Policy or our privacy practices, please contact us:
          </Typography>

          <Box sx={{ bgcolor: 'grey.50', p: 3, borderRadius: 2, mb: 3 }}>
            <Typography variant="body1">
              <strong>Email:</strong> bddashboardcrm@gmail.com
            </Typography>
            <Typography variant="body1">
              <strong>Subject Line:</strong> Privacy Policy Inquiry
            </Typography>
            <Typography variant="body1">
              <strong>Response Time:</strong> Within 48 hours
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Policy Updates
          </Typography>
          <Typography variant="body1" paragraph>
            We may update this Privacy Policy periodically to reflect changes in our practices or 
            applicable laws. We will notify you of significant changes by email and by posting the 
            updated policy on our platform.
          </Typography>

          <Alert severity="info">
            <Typography variant="body2">
              <strong>Effective Date:</strong> This Privacy Policy is effective as of {lastUpdated} and 
              applies to all information collected by BD Dashboard.
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PrivacyPolicy;